import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { PageHeader } from '../components/ui/PageHeader';
import { apiService } from '../services/apiService';
import { PaginatedResponse } from '../types';
import { useToast } from '../contexts/ToastContext';

interface APIResponse {
  id: number;
  api: number;
  api_name: string;
  search_request_id: number;
  search_value: string;
  user_email: string;
  request_url: string;
  request_headers: Record<string, any>;
  request_body: Record<string, any> | null;
  response_status: number;
  response_headers: Record<string, any>;
  response_body: Record<string, any>;
  processing_time: number;
  error_details: string;
  created_at: string;
  completed_at: string | null;
}

interface APIResponsesFilters {
  api_id: string;
  status: string;
  failed_only: boolean;
  search: string;
}

export const AdminAPIResponsesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const failedFromUrl = searchParams.get('failed_only') === 'true';
  const { showToast } = useToast();

  const [responses, setResponses] = useState<PaginatedResponse<APIResponse> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<APIResponsesFilters>({
    api_id: '',
    status: '',
    failed_only: failedFromUrl,
    search: ''
  });
  const [selectedResponse, setSelectedResponse] = useState<APIResponse | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [relatedLogs, setRelatedLogs] = useState<any[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Loading API responses with params:', {
        page: currentPage,
        page_size: pageSize,
        api_id: filters.api_id ? parseInt(filters.api_id) : undefined,
        status: filters.status || undefined,
        search: filters.search || undefined,
        ordering: '-created_at'
      });
      
      const response = await apiService.getAdminAPIResponses({
        page: currentPage,
        page_size: pageSize,
        api_id: filters.api_id ? parseInt(filters.api_id) : undefined,
        status: filters.status || undefined,
        failed_only: filters.failed_only || undefined,
        search: filters.search || undefined,
        ordering: '-created_at'
      });
      
      console.log('API Response received:', response);
      console.log('Response structure:', {
        count: response.count,
        results: response.results,
        resultsLength: response.results?.length
      });
      
      setResponses(response);
    } catch (err) {
      setError('Failed to load API responses');
      console.error('Error loading API responses:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedResponse || !showDetails) {
      setRelatedLogs([]);
      return;
    }
    setLogsLoading(true);
    apiService.getAdminAPILogs({
      search_request_id: selectedResponse.search_request_id,
      api_id: selectedResponse.api,
      page_size: 50,
      ordering: '-timestamp'
    })
      .then(res => setRelatedLogs(res.results || []))
      .catch(() => setRelatedLogs([]))
      .finally(() => setLogsLoading(false));
  }, [selectedResponse?.id, showDetails]);

  const handleFilterChange = (key: keyof APIResponsesFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 400 && status < 500) return 'warning';
    if (status >= 500) return 'error';
    return 'default';
  };

  const getStatusText = (status: number) => {
    if (status >= 200 && status < 300) return 'Success';
    if (status >= 400 && status < 500) return 'Client Error';
    if (status >= 500) return 'Server Error';
    if (status === 0) return 'Exception';
    return 'Failed';
  };

  const getTotalPages = () => {
    if (!responses || !responses.count) return 0;
    return Math.ceil(responses.count / pageSize);
  };

  const formatJsonForDisplay = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') {
      // Try to pretty-print JSON strings
      try {
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return value;
      }
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  const downloadJsonText = (filenameBase: string, jsonText: string) => {
    const safe = (filenameBase || 'api-response')
      .toString()
      .trim()
      .replace(/[^\w\-]+/g, '-')
      .replace(/\-+/g, '-')
      .replace(/^\-+|\-+$/g, '')
      .slice(0, 80);
    const blob = new Blob([jsonText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safe || 'api-response'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderPagination = () => {
    if (!responses || !responses.count || responses.count === 0) return null;

    const totalPages = getTotalPages();
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700 dark:text-gray-200">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, responses.count)} of {responses.count} responses
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Select
            value={pageSize.toString()}
            onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
            className="w-20"
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
          </Select>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          
          {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
            <Button
              key={page}
              variant={page === currentPage ? "default" : "outline"}
              size="sm"
              onClick={() => handlePageChange(page)}
            >
              {page}
            </Button>
          ))}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const renderFilters = () => (
    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg mb-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Filter API Responses</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">API ID</label>
          <Input
            type="number"
            placeholder="Filter by API ID..."
            value={filters.api_id}
            onChange={(e) => handleFilterChange('api_id', e.target.value)}
            className="w-full"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Status</label>
          <Select
            value={filters.failed_only ? 'failed' : filters.status}
            onChange={(e) => {
              const v = e.target.value;
              setFilters(prev => ({
                ...prev,
                failed_only: v === 'failed',
                status: v === 'failed' ? '' : v
              }));
              setCurrentPage(1);
            }}
            className="w-full"
          >
            <option value="">All Statuses</option>
            <option value="failed">Failed Only (errors & non-200)</option>
            <option value="200">200 - Success</option>
            <option value="400">400 - Client Error</option>
            <option value="500">500 - Server Error</option>
          </Select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Search Value</label>
          <Input
            type="text"
            placeholder="Search in values..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );

  const renderResponseDetails = () => {
    if (!selectedResponse) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80 max-w-4xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">API Response Details</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedResponse(null);
                  setShowDetails(false);
                }}
              >
                Close
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">Basic Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">API:</span> {selectedResponse.api_name}</div>
                    <div><span className="font-medium">Search Value:</span> {selectedResponse.search_value}</div>
                    <div><span className="font-medium">User:</span> {selectedResponse.user_email}</div>
                    <div><span className="font-medium">Status:</span> 
                      <Badge variant={getStatusColor(selectedResponse.response_status)} className="ml-2">
                        {selectedResponse.response_status} - {getStatusText(selectedResponse.response_status)}
                      </Badge>
                    </div>
                    <div><span className="font-medium">Processing Time:</span> {selectedResponse.processing_time}s</div>
                    <div><span className="font-medium">Created:</span> {new Date(selectedResponse.created_at).toLocaleString()}</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">Request Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">URL:</span> {selectedResponse.request_url}</div>
                    <div><span className="font-medium">Method:</span> GET</div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Request Headers</h3>
                <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs overflow-x-auto">
                  {JSON.stringify(selectedResponse.request_headers, null, 2)}
                </pre>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Request Body</h3>
                {selectedResponse.request_body ? (
                  <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs overflow-x-auto">
                    {JSON.stringify(selectedResponse.request_body, null, 2)}
                  </pre>
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded text-xs text-gray-500 dark:text-gray-400">
                    No request body (GET request)
                  </div>
                )}
              </div>
              
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Response Headers</h3>
                <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs overflow-x-auto">
                  {JSON.stringify(selectedResponse.response_headers, null, 2)}
                </pre>
              </div>
              
              <div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="font-medium text-gray-900 dark:text-white">Response Body</h3>
                  {selectedResponse.response_body != null &&
                  (typeof selectedResponse.response_body === 'object'
                    ? Object.keys(selectedResponse.response_body).length > 0
                    : String(selectedResponse.response_body).length > 0) ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const jsonText = formatJsonForDisplay(selectedResponse.response_body);
                          navigator.clipboard
                            .writeText(jsonText)
                            .then(() => showToast('Response JSON copied', 'success'))
                            .catch(() => showToast('Failed to copy response JSON', 'error'));
                        }}
                      >
                        Copy JSON
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const jsonText = formatJsonForDisplay(selectedResponse.response_body);
                          downloadJsonText(
                            `${selectedResponse.api_name || 'api'}-response-${selectedResponse.id}`,
                            jsonText
                          );
                          showToast('Download started', 'success');
                        }}
                      >
                        Download JSON
                      </Button>
                    </div>
                  ) : null}
                </div>
                {selectedResponse.response_body != null &&
                (typeof selectedResponse.response_body === 'object'
                  ? Object.keys(selectedResponse.response_body).length > 0
                  : String(selectedResponse.response_body).length > 0) ? (
                  <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs overflow-x-auto">
                    {formatJsonForDisplay(selectedResponse.response_body)}
                  </pre>
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded text-xs text-gray-500 dark:text-gray-400">
                    Empty response body (API returned no content or threw before response)
                  </div>
                )}
              </div>
              
              {selectedResponse.error_details && (
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">Error Details</h3>
                  <pre className="bg-red-50 dark:bg-red-900/20 p-3 rounded text-xs overflow-x-auto text-red-700 dark:text-red-300">
                    {selectedResponse.error_details}
                  </pre>
                </div>
              )}

              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">Related Logs</h3>
                {logsLoading ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400 py-2">Loading logs...</div>
                ) : relatedLogs.length === 0 ? (
                  <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded text-xs text-gray-500 dark:text-gray-400">
                    No logs found for this API request. Logs are captured for WARNING+ level from searches and api_details.
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded text-xs max-h-48 overflow-y-auto space-y-2">
                    {relatedLogs.map((log) => (
                      <div key={log.id} className="border-b border-gray-200 dark:border-gray-700 pb-2 last:border-0">
                        <div className="flex gap-2 text-gray-600 dark:text-gray-300">
                          <span className="font-mono">{new Date(log.timestamp).toISOString()}</span>
                          <span className={`font-medium ${log.level === 'ERROR' ? 'text-red-600 dark:text-red-400' : log.level === 'WARNING' ? 'text-amber-600' : ''}`}>
                            [{log.level}]
                          </span>
                        </div>
                        <div className="mt-1 text-gray-800 dark:text-gray-100">{log.message}</div>
                        {log.exception_info && (
                          <pre className="mt-1 text-red-700 dark:text-red-300 whitespace-pre-wrap break-words">{log.exception_info}</pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading && !responses) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner text="Loading API responses..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={filters.failed_only ? 'Failed API Calls' : 'API Responses'}
        subtitle={filters.failed_only
          ? 'APIs that failed to return data (exceptions, 4xx/5xx, timeouts). Click View Details for full request/response.'
          : 'View all API responses and their details. Monitor API performance and debug issues.'}
        align="center"
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      <Card>
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">API Responses</h2>
          
          {renderFilters()}
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner text="Loading..." />
            </div>
          ) : !responses?.results || responses.results.length === 0 ? (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No API responses found</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {filters.api_id || filters.status || filters.search 
                  ? 'Try adjusting your filters to see more results.' 
                  : 'API responses will appear here when searches are performed.'}
              </p>
            </div>
          ) : (
            <>
            
              
              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        API
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Search Value
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {responses.results.map((response) => (
                      <tr key={response.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {response.api_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            ID: {response.api}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {response.search_value}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Request ID: {response.search_request_id}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {response.user_email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant={getStatusColor(response.response_status)}>
                            {response.response_status} - {getStatusText(response.response_status)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {response.processing_time}s
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {new Date(response.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedResponse(response);
                              setShowDetails(true);
                            }}
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {renderPagination()}
            </>
          )}
        </div>
      </Card>
      
      {showDetails && renderResponseDetails()}
    </div>
  );
}; 